import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useTheme } from "../contexts/ThemeContext";

// Edit these to change the sitewide announcement. Bump the id whenever the
// message changes so visitors who dismissed the old one see the new one.
const ANNOUNCEMENT = {
  // Origin claim: only the printing is domestic — the blank garments are
  // imported. Say "printed", never "made"; the full disclosure lives on the
  // Shipping page (linked below) and in the FAQ.
  id: "printed-in-usa-v1",
  message: "Every piece printed in the USA",
  linkText: "How shipping works",
  linkTo: "/shipping",
};

const DISMISSED_KEY = "americana-announcement-dismissed";

/**
 * Thin dismissible announcement strip above the header. Hidden while the
 * America250 celebration banner is active so the two don't stack, and stays
 * hidden (per announcement id) once a visitor dismisses it.
 */
const AnnouncementBar: React.FC = () => {
  const { activeTheme } = useTheme();
  const [dismissed, setDismissed] = useState<boolean>(() => {
    try {
      return localStorage.getItem(DISMISSED_KEY) === ANNOUNCEMENT.id;
    } catch {
      return false;
    }
  });

  if (dismissed || activeTheme.id === "america250") return null;

  const handleDismiss = () => {
    setDismissed(true);
    try {
      localStorage.setItem(DISMISSED_KEY, ANNOUNCEMENT.id);
    } catch {
      // localStorage unavailable (private browsing) — dismiss for this render only
    }
  };

  return (
    <div className="announcement-bar" role="region" aria-label="Announcement">
      <div className="announcement-bar__inner">
        <span className="announcement-bar__message lora-text">
          {ANNOUNCEMENT.message}
          {ANNOUNCEMENT.linkText && (
            <>
              {" · "}
              <Link to={ANNOUNCEMENT.linkTo} className="announcement-bar__link">
                {ANNOUNCEMENT.linkText}
              </Link>
            </>
          )}
        </span>
      </div>
      <button
        onClick={handleDismiss}
        aria-label="Dismiss announcement"
        className="announcement-bar__close"
      >
        <svg
          width="12"
          height="12"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinecap="round"
          aria-hidden="true"
        >
          <line x1="18" y1="6" x2="6" y2="18" />
          <line x1="6" y1="6" x2="18" y2="18" />
        </svg>
      </button>
    </div>
  );
};

export default AnnouncementBar;
